<template>
  <div class="card reduction-section">
    <div class="card-body">
      <a-row :gutter="[16, 16]" align="middle" type="flex">
        <!-- Reduction Input -->
        <a-col :xs="24" :lg="8">
          <div v-if="reductionType === 'amount'">
            <label
              >{{
                selectedTypeFR === "R"
                  ? $t("recette.reducRecu")
                  : $t("recette.reducFact")
              }}
              ({{ getReductionTypeLabel(reductionType) }})</label
            >
            <a-input-number
              style="width: 100%"
              :min="0"
              :value="tndOnTransReduction"
              @change="handleTndReductionChange"
            />
          </div>
          <div v-else>
            <label
              >{{
                selectedTypeFR === "R"
                  ? $t("recette.reducRecu")
                  : $t("recette.reducFact")
              }}
              (%)</label
            >
            <a-input-number
              style="width: 100%"
              :min="0"
              :max="100"
              :value="onTransReduction"
              @change="handlePercentageReductionChange"
            />
          </div>
        </a-col>

        <!-- Reduction Details -->
        <a-col :xs="24" :lg="8">
          <ul class="reduction-details">
            <li>
              <span>{{ $t("recette.avantReduction") }}:</span>
              <strong>{{ formattedBeforeReduction }}</strong>
            </li>

            <li v-if="reductionType === 'amount'">
              <span>{{ $t("recette.reduction") }} (%):</span>
              <strong>{{ formattedPercentageReduction }}%</strong>
            </li>
            <li v-else>
              <span>{{ $t("recette.reduction") }} ({{ getBuildingCurrency() }}):</span>
              <strong>{{ formattedTndReduction }}</strong>
            </li>
            <li v-if="showFiscalStamp">
              <span>{{ $t("recette.timbreFiscal") }}:</span>
              <strong>{{ formattedFiscal }}</strong>
            </li>
            <li>
              <span>{{ $t("recette.apresReduction") }}:</span>
              <strong>{{ formattedAfterReduction }}</strong>
            </li>
            <li v-if="withAvance">
              <span>{{ $t("recette.avanceAPayer") }}:</span>
              <strong>{{ formattedAvanceAmount }}</strong>
            </li>
          </ul>
        </a-col>

        <!-- Actions -->
        <a-col :xs="24" :lg="8" class="reduction-actions">
          <a-button @click="handleCheckAll" type="primary">
            <a-icon type="dollar" />{{ $t("recette.paiementTotal") }}
          </a-button>
        </a-col>
      </a-row>
    </div>
  </div>
</template>

<script>
import { formatCurrency as formatCurrencyUtil, getReductionTypeLabel, getBuildingCurrency } from "@/utilities/countrySpecific";

export default {
  name: "ReductionSection",

  props: {
    reductionType: {
      required: true,
      type: String,
    },
    selectedTypeFR: {
      required: true,
      type: String,
    },
    tndOnTransReduction: {
      required: true,
      type: Number,
    },
    onTransReduction: {
      required: true,
      type: Number,
    },
    total: {
      required: true,
      type: Object,
    },
    avanceAmount: {
      required: true,
      type: Number,
    },
    withAvance: {
      required: true,
      type: Boolean,
    },
    fiscalTimbre: {
      type: Number,
      default: () => parseFloat(process.env.VUE_APP_FISCAL) || 0,
    },
    timbreApplicable: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      currency: null,
    };
  },

  created() {
    this.currency = getBuildingCurrency();
  },

  computed: {
    // Memoized computed properties for better performance
    showFiscalStamp() {
      return (
        this.selectedTypeFR === "F" &&
        this.timbreApplicable &&
        Number(this.total.aReduction) > 0
      );
    },

    formattedBeforeReduction() {
      return this.formatCurrency(this.total.bReduction);
    },
    formattedAvanceAmount() {
      return this.formatCurrency(this.avanceAmount);
    },
    formattedPercentageReduction() {
      return this.onTransReduction.toFixed(3);
    },

    formattedTndReduction() {
      return this.formatCurrency(this.total.reduction);
    },

    formattedFiscal() {
      return this.formatCurrency(this.fiscalTimbre);
    },

    afterReductionAmount() {
      const base = Number(this.total.aReduction) || 0;
      // Do not show timbre alone when no fees are selected
      if (!this.showFiscalStamp || base <= 0) return base;
      return base + Number(this.fiscalTimbre || 0);
    },

    formattedAfterReduction() {
      return this.formatCurrency(this.afterReductionAmount);
    },
  },

  methods: {
    formatCurrency(amount) {
      return formatCurrencyUtil(amount, this.currency);
    },
    getReductionTypeLabel(reductionType) {
      return getReductionTypeLabel(reductionType);
    },
    getBuildingCurrency() {
      return this.currency;
    },

    handleTndReductionChange(value) {
      // Emit the reduction type (amount)
      this.$emit("update-reduction", this.reductionType, value);
    },

    handlePercentageReductionChange(value) {
      this.$emit("update-reduction", "percentage", value);
    },

    handleCheckAll() {
      this.$emit("check-all");
    },
  },
};
</script>

<style scoped>
.reduction-section {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
  margin: 20px 0;
  border: 1px solid #e9ecef;
}

.reduction-details {
  list-style: none;
  padding: 0;
  margin: 0;
}

.reduction-details li {
  display: flex;
  justify-content: space-between;
  padding: 4px 0;
  border-bottom: 1px solid #e9ecef;
}

.reduction-details li:last-child {
  border-bottom: none;
}

.reduction-details span {
  color: #6c757d;
}

.reduction-actions {
  text-align: right;
}

@media (max-width: 991px) {
  .reduction-actions {
    text-align: left;
    margin-top: 16px;
  }
}
</style>
